/**
 * React Context and hooks for configuration management
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react';

import { ConfigManager } from './ConfigManager';
import type { AppConfig } from './types';

/**
 * Configuration context interface
 */
interface ConfigContextValue {
  config: AppConfig | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Configuration context
 */
const ConfigContext = createContext<ConfigContextValue | null>(null);

/**
 * Configuration provider props
 */
interface ConfigProviderProps {
  children: ReactNode;
}

/**
 * Configuration provider component
 */
export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const configManager = useMemo(() => ConfigManager.getInstance(), []);

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const loadedConfig = await configManager.loadConfig();
        
        // Validate that API_BASE_URL is present
        if (!loadedConfig || !loadedConfig.API_BASE_URL) {
          throw new Error('Configuration is missing required API_BASE_URL. Please configure the system properly.');
        }
        
        setConfig(loadedConfig);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration';
        const configError = new Error(
          `Configuration Error: ${errorMessage}. Please ensure /siteconfig/siteconfig.json is properly configured.`
        );
        setError(configError);
        console.error('Configuration loading failed:', configError);
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [configManager]);

  const value: ConfigContextValue = {
    config,
    isLoading,
    isLoaded: !isLoading && config !== null && error === null,
    error,
    refresh: async () => {
      let config = await configManager.getConfig();
      setConfig(config);
    },
  };

  // Show error page if configuration failed
  if (error && !isLoading) {
    return <ConfigError error={error} onRetry={() => window.location.reload()} />;
  }

  // Show loading screen while loading
  if (isLoading) {
    return <ConfigLoading message="Loading system configuration..." />;
  }

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Hook to access configuration context
 */
export const useConfig = (): ConfigContextValue => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

/**
 * Hook to get the API base URL
 */
export const useApiUrl = (): string => {
  const { config } = useConfig();
  return config?.API_BASE_URL || '';
};

/**
 * Loading component for configuration
 */
interface ConfigLoadingProps {
  children?: ReactNode;
  message?: string;
}

export const ConfigLoading: React.FC<ConfigLoadingProps> = ({
  children,
  message = 'Loading configuration...',
}) => {
  return (
    <div className="config-loading min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-label={message}>
      {children || (
        <div className="config-loading-default text-center">
          <div className="config-loading-spinner mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          </div>
          <p className="text-gray-600">{message}</p>
        </div>
      )}
    </div>
  );
};

/**
 * Error component for configuration
 */
interface ConfigErrorProps {
  error: Error;
  onRetry?: () => void;
  children?: ReactNode;
}

export const ConfigError: React.FC<ConfigErrorProps> = ({
  error,
  onRetry,
  children,
}) => {
  return (
    <div className="config-error min-h-screen flex items-center justify-center bg-gray-50" role="alert">
      {children || (
        <div className="config-error-default max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Configuration Error</h3>
          <p className="text-sm text-gray-600 mb-6">{error.message}</p>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Please ensure <code className="bg-gray-100 px-1 py-0.5 rounded">/siteconfig/siteconfig.json</code> is properly configured with required settings.
            </p>
            {onRetry && (
              <button 
                onClick={onRetry} 
                className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Retry Configuration
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Higher-order component that ensures configuration is loaded
 */
interface WithConfigProps {
  loading?: ReactNode;
  error?: (error: Error, retry: () => void) => ReactNode;
}

export const withConfig = <P extends object>(
  Component: React.ComponentType<P>,
  options: WithConfigProps = {}
) => {
  const WrappedComponent = (props: P) => {
    const { config, isLoading, error, refresh } = useConfig();

    if (isLoading) {
      return <>{options.loading || <ConfigLoading />}</>;
    }

    if (error || !config) {
      const errorElement = options.error?.(error || new Error('Configuration not available'), refresh);
      return <>{errorElement || <ConfigError error={error || new Error('Configuration not available')} onRetry={refresh} />}</>;
    }

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withConfig(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};
